import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cn } from '../../util/lib';
import { useTranslation } from 'react-i18next';
type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
  isDisabled?: boolean; // Custom prop to handle disabling the switch
  checked?: boolean; // Prop to control the checked state
  onCheckedChange?: (checked: boolean) => void; // Optional callback to notify parent of state change
};

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, isDisabled = false, checked, onCheckedChange, ...props }, ref) => {
    // If the `checked` prop is provided, we are in controlled mode
    const [isChecked, setIsChecked] = React.useState(checked ?? false);
    const { t } = useTranslation();
    // Handler to update the state of the switch
    const handleChange = (checked: boolean) => {
      if (!isDisabled) {
        if (checked !== undefined) {
          if (onCheckedChange) {
            onCheckedChange(checked); // Notify the parent of the state change in controlled mode
          }
          if (checked === undefined) {
            setIsChecked(checked); // Update internal state if uncontrolled
          }
        }
      }
    };

    return (
      <SwitchPrimitives.Root
        className={cn(
          'relative inline-flex h-[28px] w-[120px] shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-100 data-[state=checked]:bg-[#BDCDD1] data-[state=unchecked]:bg-[#BDCDD1]',
          isDisabled && 'cursor-not-allowed opacity-50', // Additional styles when disabled
          className
        )}
        {...props}
        ref={ref}
        checked={checked ?? isChecked} // Use `checked` prop for controlled or `isChecked` state for uncontrolled
        onCheckedChange={handleChange} // Handle state change
        disabled={isDisabled} // Set the disabled attribute based on the `isDisabled` prop
      >
        <SwitchPrimitives.Thumb
          className={cn(
            'pointer-events-none block h-6 w-8 rounded-full shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-[80px] data-[state=unchecked]:translate-x-0',
            'data-[state=checked]:bg-[#3BFF00]',
            'data-[state=unchecked]:bg-[#FF0000]',
            'z-10'
          )}
        />
        {/* Conditionally render ACTIVE text based on the checked state */}
        {checked ?? isChecked ? (
          <span className={cn('absolute left-1 text-black transition-opacity duration-500 ease-in-out z-0')}>ACTIVE </span>
        ) : (
          <span className={cn('absolute right-1 text-black transition-opacity duration-500 ease-in-out z-0')}>INACTIVE </span>
        )}
      </SwitchPrimitives.Root>
    );
  }
);

Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
